// src/components/singalong/LyricsDisplay.js
//
// The sing-along "screen": one page of lyrics with the current line lit word
// by word, the SBL logo large and faint behind the words, and the copyright
// line along the bottom. Pure display — it is handed the time and draws that
// moment. The portal player and the studio preview both use it, and the MP4
// renderer (renderSingAlongFrame.js) draws the same frameAt() result onto a
// canvas, so what you preview is what students and the video get.

import React from "react";
import {
  COPYRIGHT_TEXT,
  DEFAULT_LOGO_OPACITY,
  frameAt,
  logoUrlFor,
  themeOf,
  wordsOf,
} from "../../services/lyricSyncService";
import "./singalong.css";

const CountdownDots = ({ seconds }) => {
  if (!Number.isFinite(seconds) || seconds > 3.2 || seconds < 0) return null;
  const left = Math.ceil(seconds);
  return (
    <div className="sa-dots" aria-hidden="true">
      {[3, 2, 1].map((n) => (
        <span key={n} className={`sa-dot ${left >= n ? "is-on" : ""}`} />
      ))}
    </div>
  );
};

export const themeStyle = (themeKey) => {
  const theme = themeOf(themeKey);
  return {
    "--sa-bg1": theme.bg[0],
    "--sa-bg2": theme.bg[1],
    "--sa-ink": theme.ink,
    "--sa-dim": theme.dim,
    "--sa-sung": theme.sung,
    "--sa-lit": theme.lit,
    "--sa-pill": theme.pill,
    "--sa-band": theme.band,
  };
};

const LyricsDisplay = ({
  parsed,
  cues,
  time,
  themeKey,
  title,
  showTitle = true,
  logoOpacity = DEFAULT_LOGO_OPACITY,
  onLineClick,
  // 1 = fully visible, 0 = faded out. Driven by fadeLevelAt() after the
  // "End sing-along here" marker.
  fade = 1,
  className = "",
}) => {
  const theme = themeOf(themeKey);
  const frame = frameAt(parsed, cues || [], time || 0);
  const style = themeStyle(themeKey);

  const backdrop = (
    <>
      {logoOpacity > 0 && (
        <img
          className="sa-watermark"
          src={logoUrlFor(themeKey)}
          alt=""
          aria-hidden="true"
          draggable="false"
          style={{ opacity: logoOpacity }}
        />
      )}
      <div className="sa-copyright">{COPYRIGHT_TEXT}</div>
      {fade < 1 && (
        <div className="sa-fade-veil" aria-hidden="true" style={{ opacity: 1 - Math.max(0, fade) }} />
      )}
    </>
  );

  if (frame.mode === "empty") {
    return (
      <div className={`sa-stage sa-${theme.family} ${className}`} style={style}>
        {backdrop}
        <div className="sa-empty">No lyrics to show yet.</div>
      </div>
    );
  }

  const { page } = frame;

  return (
    <div className={`sa-stage sa-${theme.family} is-${frame.mode} ${className}`} style={style}>
      {backdrop}

      {showTitle && title ? <div className="sa-title">{title}</div> : null}

      <div className="sa-label-row">
        {page.label ? (
          <span className="sa-pill">
            {page.label}
            {page.parts > 1 ? ` · ${page.part}/${page.parts}` : ""}
          </span>
        ) : null}
      </div>

      <div className="sa-lines" aria-live="off">
        {page.lineIdx.map((i) => {
          const state = frame.lineState[i] || "upcoming";
          const words = wordsOf(parsed.lines[i]);
          return (
            <p
              key={`${frame.pageIndex}-${i}`}
              className={`sa-line is-${state} ${onLineClick ? "is-clickable" : ""}`}
              onClick={onLineClick ? () => onLineClick(i) : undefined}
            >
              {words.map((w, k) => (
                <span
                  key={k}
                  className={`sa-word ${
                    state === "active" && k < (frame.litWords || 0) ? "is-lit" : ""
                  }`}
                >
                  {w}
                  {k < words.length - 1 ? " " : ""}
                </span>
              ))}
            </p>
          );
        })}
      </div>

      <div className="sa-foot">
        {frame.mode === "intro" && (
          <div className="sa-cue-text">
            Get ready to sing!
            <CountdownDots seconds={frame.countdown} />
          </div>
        )}
        {frame.mode === "break" && (
          <div className="sa-cue-text">
            ♪ Music break ♪
            <CountdownDots seconds={frame.countdown} />
          </div>
        )}
        {frame.mode === "end" && <div className="sa-cue-text">⭐ Great singing! ⭐</div>}
        {frame.mode === "sing" && frame.nextPreview && (
          <div className="sa-next">
            <span className="sa-next-tag">
              Next{frame.nextPreview.label ? ` · ${frame.nextPreview.label}` : ""}
            </span>
            {frame.nextPreview.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default LyricsDisplay;
